import { call, put, takeLatest } from "redux-saga/effects";

import { DataType, FeatureType } from "@/jenesys-types/data/enums";
import { Transaction } from "@/jenesys-types/jenesys-entities/config";

import { performFeatureAction } from "@/services/api";
import { loadUpdateItem } from "@/store/crud/update";
import { redirect } from "@/store/redirection/redirection";

import {
  fetchAdjacentDocumentFailure,
  fetchAdjacentDocumentRequest,
  FetchAdjacentDocumentRequestAction,
  fetchAdjacentDocumentSuccess,
  IDocument
} from "./entityNavigation";

// API function to fetch adjacent document (now using performFeatureAction)
function* callFetchAdjacentDocumentApi(params: {
  companyId: string;
  documentId: string;
  direction: "previous" | "next";
  status: string;
}): Generator<any, IDocument | null, any> {
  const { companyId, documentId, direction, status } = params;

  const queryParams = {
    order_by: ["status_order:asc", "date_of_invoice:desc", "supplier_name:asc"],
    document_status: status
  };

  const pathParams = {
    companyId,
    documentId,
    direction
  };

  // Call performFeatureAction
  const data: Transaction | null = yield call(
    performFeatureAction,
    FeatureType.Bookkeeping,
    "fetchAdjacentDocument",
    pathParams,
    queryParams
  );
  return data;
}

// Saga to handle fetching adjacent document
function* fetchAdjacentDocumentSaga(
  action: FetchAdjacentDocumentRequestAction
): Generator<any, void, any> {
  try {
    const document: Transaction | null = yield call(
      callFetchAdjacentDocumentApi,
      action.payload
    );

    if (document) {
      const { companyId } = action.payload;
      const transactionId = document.id;
      // /client/d3ec5cc7-6da7-4b4a-8e6e-0cf0d2aecaa3/transactions/management/379df99a-4365-4f48-a52f-895df8076500/edit

      const redirectToPath = `/client/${companyId}/transactions/management/${transactionId}/edit`;

      // Put the fetched document into the store so the UI can update
      // and include meta for redirection
      yield put(
        loadUpdateItem(
          DataType.Transaction,
          document.id,
          undefined, // pathParams
          document, // preFetchedItem
          { redirectTo: redirectToPath } // meta
        )
      );
    } else if (action.meta.onFinalDocument?.redirectTo) {
      yield put(redirect(action.meta.onFinalDocument.redirectTo));
    }
    yield put(fetchAdjacentDocumentSuccess(document, action.payload.direction));
  } catch (error: unknown) {
    if (action.meta.onFailure?.redirectTo) {
      yield put(redirect(action.meta.onFailure.redirectTo));
    }
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error occurred";
    yield put(fetchAdjacentDocumentFailure(errorMessage));
  }
}

// Root saga for entity navigation
export default function* entityNavigationSaga(): Generator<any, void, any> {
  yield takeLatest(
    fetchAdjacentDocumentRequest.type,
    fetchAdjacentDocumentSaga
  );
}
